import {
  isValid as isValidDateFns,
  isPast,
  formatDistanceStrict,
  intervalToDuration,
  differenceInSeconds,
  differenceInHours,
  parseISO,
  format,
  type Locale,
  isValid,
  isFuture,
} from "date-fns";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import type { Nullable, NullableMaybe } from "@/types/utilities";
import {
  getLocale,
  type LocaleValue,
  MONTH_FIRST_COUNTRIES,
} from "@/utilities/locale";
import { enAU, enUS, enCA, enNZ } from "date-fns/locale";
import { getTimezone } from "countries-and-timezones";
import type { DateTimeFormatConfig } from "@/utilities/dateTimeFormats";

const DATE_FNS_LOCALE_MAP: Record<LocaleValue | "en-nz", Locale> = {
  "en-au": enAU,
  "en-us": enUS,
  "en-ca": enCA,
  "en-nz": enNZ,
};

/**
 * @description Wraps date-fns for use as a type guard
 */
export function isValidDateGuard(date: unknown): date is Date {
  return date instanceof Date && isValidDateFns(date);
}

export function isValidFutureDate(date: string) {
  if (!date) return false;
  const parsedDate = parseISO(date);
  return isValid(parsedDate) && isFuture(parsedDate);
}

// Below this we show exact minutes/seconds; above it, a coarser unit.
export const FIVE_MINUTES_IN_SECONDS = 5 * 60;

/**
 * @description Return a description of the duration between now and datems, using our abbreviated unit format. E.g. 1D, 3H, 3Mo
 * @param datems the date
 * @param secondsToNow pass this if you already have it (e.g. from a shared clock), otherwise it's computed from the real current time
 */
export function formatDistanceToNowAbbr(
  datems: Date | null,
  skipPrefix = false,
  secondsToNow?: number,
) {
  if (!isValidDateGuard(datems)) return "-";

  const duration = secondsToNow ?? differenceInSeconds(datems, new Date());
  const now = new Date(datems.getTime() - duration * 1000);

  const isInPast = duration < 0;
  const abs = Math.abs(duration);
  const prefix = isInPast && !skipPrefix ? "-" : "";

  if (abs < 60) {
    return `${prefix}${abs}S`;
  }

  if (abs < FIVE_MINUTES_IN_SECONDS) {
    const interval = intervalToDuration({
      start: isInPast ? datems : now,
      end: isInPast ? now : datems,
    });

    return `${prefix}${interval.minutes ?? 0}M ${interval.seconds ?? 0}S`;
  }

  return (
    prefix +
    formatDistanceStrict(datems, now, {
      locale: {
        ...enAU,
        formatDistance: (token: keyof typeof formatDistanceAbbr, count) =>
          formatDistanceAbbr[token].replace("{{count}}", String(count)),
      },
    })
  );
}

/**
 * @description Return a description of the duration between date and given date, using our abbreviated unit format. E.g. 1D, 3H, 3Mo
 * @param date the date
 */
export function formatDistanceToDateFromNowAbbr(
  date: Date,
  skipPrefix = false,
) {
  if (!date) {
    return "-";
  }

  const prefix = isPast(date) && !skipPrefix ? "-" : "";

  return (
    prefix +
    formatDistanceStrict(new Date(), date, {
      roundingMethod: "floor",
      locale: {
        ...enAU,
        formatDistance: (token: keyof typeof formatDistanceAbbr, count) =>
          formatDistanceAbbr[token].replace("{{count}}", String(count)),
      },
    })
  );
}

const formatDistanceAbbr = {
  lessThanXSeconds: "{{count}}S",
  xSeconds: "{{count}}S",
  halfAMinute: "30S",
  lessThanXMinutes: "{{count}}M",
  xMinutes: "{{count}}M",
  aboutXHours: "{{count}}H",
  xHours: "{{count}}H",
  xDays: "{{count}}D",
  aboutXWeeks: "{{count}}W",
  xWeeks: "{{count}}W",
  aboutXMonths: "{{count}}Mo",
  xMonths: "{{count}}Mo",
  aboutXYears: "{{count}}Y",
  xYears: "{{count}}Y",
  overXYears: "{{count}}Y",
  almostXYears: "{{count}}Y",
};

/**
 * Formats a date/time value using the current user's locale and country from the browser timezone.
 *
 * Resolves the app locale via i18n to select the appropriate format string from
 * the provided config, falling back to `config.default` if no match is found.
 * The user's timezone is used for offset calculation, falling back to Australia/Brisbane.
 *
 * @param dateTime - The date to format, either a Date object or ISO string
 * @param config - A map of format strings plus a required default.
 *                 Format strings use date-fns-tz tokens e.g. "dd MMM yy '@' h:mma"
 * @param timezoneOverride - DO NOT USE - used for tests so we can set the timezone
 * @param localeOverride - DO NOT USE - used for tests so we can set the locale
 * @returns The formatted date string, or null if the date is invalid
 *
 * @example
 * formatUserDateTime(new Date(), {
 *   monthFirst: "MMM d, yy '@' h:mma",
 *   default: "dd MMM yy '@' h:mma",
 * });
 */
export function formatUserDateTime(
  dateTime: NullableMaybe<Date | string>,
  config: DateTimeFormatConfig,
  timezoneOverride?: string,
  localeOverride?: LocaleValue,
) {
  const parsedDate = parseDate(dateTime);
  if (parsedDate === null) {
    return null;
  }

  const timeZone =
    timezoneOverride ??
    Intl.DateTimeFormat()?.resolvedOptions()?.timeZone ??
    "Australia/Brisbane";

  const tz = getTimezone(timeZone);
  const country = tz?.countries?.[0] ?? "AU";

  // NZ doesn't have the locale in the url or translations, so it's actually on the US locale.
  // But it should be treated like AU which is default/date first in the configs
  const locale = localeOverride ?? getLocale();

  const formatOptionKey = MONTH_FIRST_COUNTRIES.has(country)
    ? "alternative"
    : "default";

  const formatStr = config[formatOptionKey] ?? config.default;
  const dateFnsLocale =
    (locale === "en-us" && country === "NZ"
      ? DATE_FNS_LOCALE_MAP["en-nz"]
      : DATE_FNS_LOCALE_MAP[locale]) ?? DATE_FNS_LOCALE_MAP["en-us"];

  return formatInTimeZone(parsedDate, timeZone, formatStr, {
    locale: dateFnsLocale,
  });
}

/**
 * Stripped down version of above, that only takes a date and doesn't care about the timezone - will just map to default or alternative formats based on timezone
 * @param dateString - "yyyy-MM-dd"
 * @param config - A map of format strings plus a required default.
 *                 Format strings use date-fns-tz tokens e.g. "dd MMM yy '@' h:mma"
 * @param timezoneOverride - DO NOT USE - used for tests
 */
export function formatUserDate(
  dateString: NullableMaybe<string>,
  config: DateTimeFormatConfig,
  timezoneOverride?: string,
) {
  const dateParts = dateString?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const date = dateParts?.length
    ? new Date(
        Number(dateParts[1]),
        Number(dateParts[2]) - 1,
        Number(dateParts[3]),
      )
    : undefined;

  if (!date) {
    return null;
  }

  const timeZone =
    timezoneOverride ??
    Intl.DateTimeFormat()?.resolvedOptions()?.timeZone ??
    "Australia/Brisbane";

  const tz = getTimezone(timeZone);
  const country = tz?.countries?.[0] ?? "AU";

  const formatOptionKey = MONTH_FIRST_COUNTRIES.has(country)
    ? "alternative"
    : "default";

  const formatStr = config[formatOptionKey] ?? config.default;
  return format(date, formatStr);
}

/**
 * @description Parses a date string known to be in AEST into a Date object in UTC
 * @param aestDateString The date string, assumed to be in AEST (e.g. "2025-07-26 15:30")
 */
export function convertAESTToDateObject(aestDateString: string): Date | null {
  if (!aestDateString) {
    return null;
  }

  const dateObjectInUtc = fromZonedTime(aestDateString, "Australia/Brisbane");

  return isValidDateGuard(dateObjectInUtc) ? dateObjectInUtc : null;
}

/**
 * Converts seconds to miliseconds
 */
export const msFromSeconds = (seconds: number) => seconds * 1000;

/**
 * Converts timestamp seconds to a Date
 */
export const dateFromSeconds = (seconds: number) =>
  new Date(msFromSeconds(seconds));

export const getHoursUntil = (date: Date | string) => {
  const parsedDate = parseDate(date);

  if (!isValidDateGuard(parsedDate) || parsedDate === null) {
    return Number.POSITIVE_INFINITY;
  }

  return differenceInHours(parsedDate, new Date());
};

export function parseDate(date: NullableMaybe<string | Date>): Nullable<Date> {
  if (!date) {
    return null;
  }

  if (typeof date === "string") {
    const hasTimezone = Boolean(date.match(/(Z|[\+\-][\d:]*)$/));
    if (!hasTimezone) {
      date += "Z";
    }
    date = parseISO(date as string);
  }

  return isValidDateGuard(date) ? date : null;
}

/**
 * Converts a wall-clock time ("HH:mm" or "H:mm") defined in a source timezone
 * into the user's local timezone and formats it, e.g. "6:00" ET → "8pm".
 *
 * Anchored to the current date in the source zone so DST offsets (e.g. EST vs
 * EDT) are applied correctly. Minutes are dropped when zero ("6am" not "6:00am").
 *
 * @param time - Wall-clock time in the source timezone, e.g. "6:00" or "18:30"
 * @param sourceTimeZone - IANA zone the time is defined in (e.g. "America/Toronto")
 * @param timezoneOverride - DO NOT USE - used for tests so we can set the timezone
 */
export function convertTimeToUserTimeZone(
  time: string,
  sourceTimeZone: string,
  timezoneOverride?: string,
) {
  const userTimeZone =
    timezoneOverride ??
    Intl.DateTimeFormat().resolvedOptions().timeZone ??
    "Australia/Brisbane";

  const [hour = 0, minute = 0] = time.split(":").map(Number);

  const pad = (value: number) => String(value).padStart(2, "0");

  // Anchor to today's date in the source timezone so DST is correct
  const sourceDate = formatInTimeZone(new Date(), sourceTimeZone, "yyyy-MM-dd");

  const instant = fromZonedTime(
    `${sourceDate}T${pad(hour)}:${pad(minute)}:00`,
    sourceTimeZone,
  );

  const hasMinutes = formatInTimeZone(instant, userTimeZone, "mm") !== "00";

  return formatInTimeZone(
    instant,
    userTimeZone,
    hasMinutes ? "h:mmaaa" : "haaa",
  );
}
